/** @format */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import {
	NgForm,
	UntypedFormControl,
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
	UntypedFormArray,
	ValidatorFn,
	FormArray,
} from '@angular/forms';

import {
	SetSearchItemsUrl,
	DnvSearchState,
	SearchItem,
	SetMapState,
	DnvMapState,
	DnvMapAction,
	CenterMap,
	SetZoom,
	DnvMapInfo,
	DnvMarker,
	SetMarkers,
	DnvLatLng,
	SetMapShowFindMe,
	SetMapClusterView,
	PlainMapFunctionsOnly,
} from 'dnv-lib';

import { Category } from '../../models/category';

import {
	ApplicationState,
	getApplicationState,
	ProblemSubType,
	SetSelectedProblemType,
	ProblemSource,
	getProblemSources,
	getSelectedProblemType,
	getDnvSearchState,
	GetMenuOption,
	AddCaseGotoStep,
	SetNewCaseNo,
	AddProblem,
	ProblemType,
	User as RapUser,
	getAllUsers,
	userFirstNameLastName,
	SearchCoordinates,
	SearchCoordinatesPayload,
	getSearchCoordinate,
	getPropertyOwners,
	SearchFromCoordinates,
	SetProblems,
	DelayedAction,
	DelayedActionBy1,
	SetSearchCoordinates,
	LookupOwnerReset,
	GetNewCaseMenu,
	ProblemMenuConfig,
	getNewCaseMenu,
	ProblemAssignment,
	getProblemAssignments,
	LastCreatedCaseNo,
	MenuItem,
	getMenus,
	ContactType,
	SetZoneLookupOnly,
	SetProblemContacts,
	Role,
	Group,
	ProblemPriority,
	getProblemPriorities,
	IsNation,
	getMapState,
	ProblemReceived,
	getProblemReceivedFrom,
	getSelectedProblemSubTypes,
	GetAllProblemSubTypes,
	SelectedProblemSubType,
	getTransferringToCityworks,
} from '../../state-management';

import { ConfigService } from '../../services/config.service';
import { ProblemDB, ContactDB, Problem, ProblemContact, ContactPhone } from 'src/app/models/problem';
import { environment } from 'src/environments/environment';
import { updatecloneRow } from '@syncfusion/ej2-grids';

@Component({
	selector: 'app-add-case',
	templateUrl: './add-case.component.html',
	styleUrls: ['./add-case.component.scss'],
	standalone: false,
})
export class AddCaseComponent implements OnInit {
	newCaseMenu$: Observable<ProblemMenuConfig[]>;
	problemAssignments$: Observable<ProblemAssignment[]>;
	problemAssignments: ProblemAssignment[] = [];
	selectedProblemType$: Observable<ProblemSubType>;
	problemSubTypes$: Observable<SelectedProblemSubType[]>;
	locationCoordinate$: Observable<SearchCoordinatesPayload | null>;
	propertyOwners$: Observable<ContactDB[]>;
	location: SearchCoordinatesPayload | null;
	zoneLookup: boolean;
	unassigned = false;
	mapLocationRequired = false;
	problemLatLng: DnvLatLng;
	propertyOwners: ContactDB[];
	menu$: Observable<MenuItem>;
	menu: MenuItem;
	configMenu: ProblemMenuConfig;
	reassignmentVisible = false;
	subTypesRequired = false;

	caseStep: number;
	newCaseForm: UntypedFormGroup;
	lastCreatedCaseNo?: LastCreatedCaseNo;
	problemSourceList$: Observable<ProblemSource[]>;
	problemReceivedFromList$: Observable<ProblemReceived[]>;
	problemPriorityList$: Observable<ProblemPriority[]>;
	problemTypesList: ProblemType[];

	assignedToUserOrRole: UserOrRole;
	assignedToGroup: Group;
	assignedSelected: string;
	newAssignmentList: UserOrRole[] = [];
	assigned: UserOrRole;
	assignedGroup: Group;
	isNation: string;

	application$: Observable<ApplicationState>;
	dnvSearchState$: Observable<DnvSearchState>;
	mapState$: Observable<DnvMapState>;

	transferringToCityworks$: Observable<boolean>;
	transferringToCityworks: boolean = true;

	private ngUnsubscribe: Subject<void> = new Subject();

	problemDescriptionMaxLength = 2000;
	selectedProblemType: ProblemType;
	selectedProblemSubTypes: SelectedProblemSubType[] = [];
	problemSubTypes: SelectedProblemSubType[];
	allUsers$: Observable<RapUser[]>;
	markers: DnvMarker[];

	originalGroup: Group;
	originalUserOrRole: UserOrRole;

	constructor(private store: Store<{}>, private configService: ConfigService, private formBuilder: UntypedFormBuilder) {
		this.application$ = this.store.select(getApplicationState);
		this.dnvSearchState$ = this.store.select(getDnvSearchState);
		this.problemSourceList$ = this.store.select(getProblemSources);
		this.problemReceivedFromList$ = this.store.select(getProblemReceivedFrom);
		this.problemPriorityList$ = this.store.select(getProblemPriorities);
		this.mapState$ = this.store.select(getMapState);
		this.transferringToCityworks$ = this.store.select(getTransferringToCityworks).pipe(takeUntil(this.ngUnsubscribe));

		this.newCaseForm = createFormGroup(this.problemDescriptionMaxLength, this.formBuilder);

		this.newCaseMenu$ = this.store.select(getNewCaseMenu);
		this.problemAssignments$ = this.store.select(getProblemAssignments);

		this.selectedProblemType$ = this.store.select(getSelectedProblemType);
		this.problemSubTypes$ = this.store.select(getSelectedProblemSubTypes);

		this.locationCoordinate$ = this.store.select(getSearchCoordinate);
		this.propertyOwners$ = this.store.select(getPropertyOwners);
		this.location = null;
		this.problemLatLng = { lat: 0, lng: 0 };
		this.propertyOwners = [];

		this.allUsers$ = this.store.select(getAllUsers);

		this.markers = [];

		this.menu$ = this.store.select(getMenus);
	}

	get pnArr() {
		return this.newCaseForm
			.get('step4')
			.get('phoneSection')
			.get('phoneNumArray') as FormArray;
	}

	ngOnInit() {
		this.store.dispatch(new GetAllProblemSubTypes(null));
		this.store.dispatch(new SetSearchCoordinates(null));
		this.store.dispatch(new LookupOwnerReset());
		this.store.dispatch(new SetProblemContacts([]));
		this.store.dispatch(new SetMapClusterView(false));
		this.store.dispatch(new SetMapShowFindMe(true));
		this.store.dispatch(new PlainMapFunctionsOnly(true));
		this.store.dispatch(new IsNation(''));

		this.application$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((app) => {
			this.caseStep = app.caseStep;
			this.lastCreatedCaseNo = app.lastCreatedCaseNo;
			this.problemTypesList = app.menu.allProblemTypeList;
			this.zoneLookup = app.zoneLookupOnly;
			this.isNation = app.isNation;
		});

		this.transferringToCityworks$
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe((transferStatus) => (this.transferringToCityworks = transferStatus));

		this.problemSubTypes$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((subTypes) => (this.problemSubTypes = subTypes));

		this.problemAssignments$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((problemAssignments) => {
			this.problemAssignments = problemAssignments;
		});

		this.menu$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((menu) => {
			if (menu) {
				this.menu = menu;
			}
		});

		this.locationCoordinate$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((location: SearchCoordinatesPayload | null) => {
			if (location) {
				this.location = location;
				const searchBar = this.newCaseForm.get('step2.addressSearch').value;

				if (!location.name.startsWith('Out of Boundary') && this.zoneLookup === false) {
					this.newCaseForm.get('step2.addressSearch').setValue({ name: location.name, id: 0, type: '', updatedBy: location.updatedBy });
					this.newCaseForm.get('step2.addressSearchOrigin').setValue(location.name);
				}

				// let marker: DnvMarker;

				const latLng: DnvLatLng = {
					lat: location.success.lat,
					lng: location.success.lng,
				};

				this.problemLatLng = latLng;

				// marker = {
				//   url: new Date().toISOString(),
				//   centerPoint: latLng,
				//   popup: '',
				//   popupOptions: { maxWidth: 400 },
				//   tooltip: location.name,
				//   tooltipOptions: {
				//     permanent: false,
				//     direction: 'right',
				//     offset: [14, -20],
				//     className: 'map-tooltip'
				//   },
				//   clickEvent: true,
				//   id: location.name,
				//   isActive: false,
				//   activeIcon: {
				//     iconFAUrl: '<i class="fas fa-map-marker-alt fa-3x"></i>',
				//     className: 'myDivIconSelected',
				//     iconSize: [35, 60],
				//   },
				//   icon: {
				//     iconFAUrl: '<i class="fas fa-map-marker-alt fa-3x"></i>',
				//     className: 'myDivIcon',
				//     iconSize: [35, 60],
				//   }
				// };

				// this.store.dispatch(new SetMarkers([marker]));
				// this.store.dispatch(new SetMarkers([]));
				this.store.dispatch(
					new DelayedAction([
						// new SetMarkers([marker]),
						new DelayedAction([new CenterMap({ lat: location.success.lat, lng: location.success.lng }), new SetZoom(Number(5))]),
					]),
				);
			} else {
				this.problemLatLng = { lat: 0, lng: 0 };
				this.store.dispatch(new SetMarkers([]));
			}
		});

		this.propertyOwners$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((po: ContactDB[]) => {
			this.propertyOwners = po;
		});

		this.mapState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((mapState) => {
			this.markers = mapState.markers;

			if (mapState.markers.length > 0 && mapState.markers[0].icon.className === 'myDivIcon') {
				this.newCaseForm
					.get('step2')
					.get('mapLocation')
					.setValue(true);
				this.mapLocationRequired = false;
			} else {
				this.newCaseForm
					.get('step2')
					.get('mapLocation')
					.setValue(null);
				this.mapLocationRequired = true;
			}
		});

		// This prevents the add-location map loading problem markers. Could be changed to handle this bug more elegantly?
		this.store.dispatch(new SetProblems([]));

		this.store.dispatch(new AddCaseGotoStep(1));
		this.store.dispatch(new GetMenuOption());
		// this.store.dispatch(new GetCategorySubType());
		this.store.dispatch(new GetNewCaseMenu());
		// this.store.dispatch(new SetSearchItemsUrl(`${environment.searchApiEndpointUrl}api/`));
		this.store.dispatch(new SetSelectedProblemType({ problemType_Id: 0, problemType_Name: 'Add a Problem' }));
		this.store.dispatch(new SetMarkers([]));
		this.store.dispatch(new SetMapState(this.configService.initialDnvMapState()));
	}

	nextStep(gotoStep: number) {
		if (!this.getDisabled()) {
			this.store.dispatch(new AddCaseGotoStep(gotoStep));
		}

		if (gotoStep === 3) {
			this.evaluateProblemAssignment();
		}

		this.refreshMarkers(gotoStep);
	}

	prevStep(gotoStep: number) {
		if (gotoStep === 1) {
			this.store.dispatch(new SetSelectedProblemType({ problemType_Id: 0, problemType_Name: 'Add a Problem' }));

			//Reset search address if someone choose another category.
			this.store.dispatch(new SetMarkers([]));
			this.store.dispatch(new IsNation(''));
			this.newCaseForm.get('step2.addressSearch').setValue({ name: '', id: 0, type: '', updatedBy: '' });
			this.newCaseForm.get('step2.addressSearchOrigin').setValue('');
		}
		this.store.dispatch(new AddCaseGotoStep(gotoStep));

		this.refreshMarkers(gotoStep);
	}

	refreshMarkers(gotoStep: number) {
		if (2 === gotoStep) {
			const markers = this.markers;
			this.store.dispatch(new SetMarkers([]));
			this.store.dispatch(new DelayedAction([new SetMarkers(markers)]));

			if (markers.length > 0) {
				this.store.dispatch(
					new DelayedAction([
						new SetMapState(this.configService.initialDnvMapState()),

						new DelayedActionBy1([new SetMarkers(markers), new CenterMap(markers[0].centerPoint), new SetZoom(Number(5))]),
					]),
				);
			}
		}
	}

	createNewCase(gotoStep: number) {
		this.store.dispatch(new AddProblem(this.buildProblem()));

		// Reset state so address search doesn't re populate with previous value
		this.store.dispatch(new SetNewCaseNo(null));
		this.store.dispatch(new SetSearchCoordinates(null));
		this.store.dispatch(new AddCaseGotoStep(gotoStep));
		this.newCaseForm = createFormGroup(this.problemDescriptionMaxLength, this.formBuilder);
		this.store.dispatch(new DelayedAction([new SetMapState(this.configService.initialDnvMapState())]));
		this.propertyOwners = [];
	}

	selectCategory(pt: ProblemMenuConfig) {
		this.newAssignmentList = [];
		this.reassignmentVisible = false;
		this.assigned = null;
		this.unassigned = false;
		this.configMenu = pt;

		this.store.dispatch(new AddCaseGotoStep(2));
		this.selectedProblemType = this.problemTypesList.filter((p) => p.problemType_Id === pt.problemType_RefId)[0]; // TODO problemTypesList ???
		if (pt.problemType_RefId > 0) {
			this.store.dispatch(new SetSelectedProblemType(this.selectedProblemType));
			this.selectedProblemSubTypes = this.problemSubTypes.filter((pst) => pst.problemType_RefId === this.selectedProblemType.problemType_Id);
			this.newCaseForm.get('step3.problemSubType').setValue('');

			if (this.selectedProblemType.problemType_Name === 'Nuisance dogs') {
				this.newCaseForm.get('step3.problemSubType').addValidators(Validators.required);
				this.subTypesRequired = true;
			} else {
				this.newCaseForm.get('step3.problemSubType').removeValidators(Validators.required);
				this.subTypesRequired = false;
			}
		}
		this.refreshMarkers(2);
	}

	locationSelectedFromList(item: SearchItem) {
		// Need to reset assignment if a new location is selected.
		this.newAssignmentList = [];
		this.reassignmentVisible = false;
		this.assigned = null;
		this.unassigned = false;

		this.newCaseForm.get('step2.addressSearchOrigin').setValue(item.name);

		this.store.dispatch(new SearchCoordinates({ ...item, problemMenu: this.configMenu }));
		// console.log('locationSelectedFromList', item.name);
	}

	buildProblem() {
		let newContact: ProblemContact[];
		const problemAssignments: ProblemAssignment[] = this.problemAssignments.filter(
			(pa) => pa.problemType_RefId === this.selectedProblemType.problemType_Id && pa.group_RefId === this.newCaseForm.value.step1.group,
		);
		let userId = null;
		let roleId = null;

		if (problemAssignments.length === 1) {
			const pa: ProblemAssignment = problemAssignments[0];
			userId = pa.user_RefId;
			roleId = pa.role_RefId;
		} else {
			if (this.location && this.location.searchLocation) {
				problemAssignments.forEach((pa) => {
					this.location.searchLocation.forEach((location) => {
						if (pa.locationType === location.locationType && pa.location === location.location) {
							userId = pa.user_RefId;
							roleId = pa.role_RefId;
						}
					});
				});
			}
		}

		if (!this.isContactFormEmpty()) {
			// get phone number(s)
			const phones = [] as ContactPhone[];
			const phoneArray = this.newCaseForm
				.get('step4')
				.get('phoneSection')
				.get('phoneNumArray') as UntypedFormArray;

			for (let i = 0; i < phoneArray.length; i++) {
				const phoNum = phoneArray.at(i).get('phoneNum').value;
				if (phoNum.length > 0) {
					const phone: ContactPhone = {
						phone: phoNum,
						phoneType_RefId: phoneArray.at(i).get('phoneType').value.phoneType_Id,
					};
					phones.push(phone);
				}
			}

			newContact = [
				{
					firstName: this.newCaseForm.value.step4.firstName,
					lastName: this.newCaseForm.value.step4.lastName,
					email: this.newCaseForm.value.step4.email,
					street: this.newCaseForm.value.step4.address,
					contactType: 'Reporter',
					phone: phones,
					notes: this.newCaseForm.value.step4.notes,
					companyName: this.newCaseForm.value.step4.companyName,
				},
			];
		} else {
			newContact = [];
		}

		// only keep the first 3 owners even if there are more
		const firstThreeOwners = this.propertyOwners.slice(0, 3);
		firstThreeOwners.forEach((o) => (o.notes = 'Owner of ' + this.newCaseForm.value.step2.addressSearch.name));

		const newProblem: ProblemDB = {
			problem: {
				caseNo: '',
				group_RefId: this.assignedGroup ? this.assignedGroup.group_Id : this.newCaseForm.value.step1.group,
				problemType_RefId: this.selectedProblemType.problemType_Id,
				problemSubType_RefId: this.newCaseForm.value.step3.problemSubType
					? this.newCaseForm.value.step3.problemSubType.problemSubType_Id
					: null,
				problemPriority_RefId: this.newCaseForm.value.step3.problemPriority.problemPriority_Id,
				description: this.newCaseForm.value.step3.problemDescription,
				problemStatus_RefId: 1,
				problemSource_RefId: this.newCaseForm.value.step3.problemSource?.problemSource_Id,
				problemReceivedFrom_RefId: this.newCaseForm.value.step3.problemReceivedFrom.problemReceivedFrom_Id,
				// Default assignment is overriden here.
				user_RefId:
					this.assigned && this.assigned.type === 'user' && this.assigned.name !== 'Unassign'
						? this.assigned.id
						: this.assigned
						? null
						: userId,

				role_RefId: this.assigned && this.assigned.type === 'role' ? this.assigned.id : this.assigned ? null : roleId,
				location_Description: this.newCaseForm.value.step2.addressSearch.name,
				location_Description_Origin: this.newCaseForm.value.step2.addressSearchOrigin,
				location_Longitude: this.problemLatLng.lng,
				location_Latitude: this.problemLatLng.lat,
				createdBy: 0,
			},
			reporter: newContact,
			owner: firstThreeOwners,
			shouldNotify: this.newCaseForm.value.step3.notifyAssignees,
		};

		return newProblem;
	}

	onMapUpdate(action: any) {
		if (action.type) {
			this.store.dispatch(action);
		} else if (action.length == 1) {
			this.store.dispatch(action[0]);
		} else {
			action.forEach((act: DnvMapAction) => {
				this.store.dispatch(act);
			});
		}
	}

	onMoveChange(state: DnvMapState) {
		this.store.dispatch(new CenterMap({ lat: state.centerPoint.lat, lng: state.centerPoint.lng }));
	}

	onZoomChange(state: DnvMapState) {
		this.store.dispatch(new SetZoom(state.zoomLevel));
	}

	onMapClick(info: DnvMapInfo) {
		this.store.dispatch(new LookupOwnerReset());
		// Need to reset assignment if a new location is selected.
		this.newAssignmentList = [];
		this.reassignmentVisible = false;
		this.assigned = null;
		this.unassigned = false;
		// this.store.dispatch(new SearchCoordinatesSuccess(null));
		this.store.dispatch(new SetMarkers([]));
		this.store.dispatch(new SetZoneLookupOnly(false));
		this.store.dispatch(
			new SearchFromCoordinates({
				problemMenu: this.configMenu,
				dnvMapInfo: info,
				zoneLookupOnly: false,
			}),
		);
	}

	getDisabled() {
		let valid = false;

		switch (this.caseStep) {
			case 1:
				valid = true;
				break;
			case 2:
				valid = this.newCaseForm.get('step2').valid;
				break;
			case 3:
				valid = this.newCaseForm.get('step3').valid;
				break;
			case 4:
				valid = this.newCaseForm.get('step4').valid;
				break;
			case 5:
				valid = true;
				break;
			default:
				break;
		}

		return !valid;
	}

	evaluateProblemAssignment() {
		if (this.newAssignmentList.length === 0) {
			const p: ProblemDB = this.buildProblem();
			let user: string, role: string, assigned: RapUser;
			if (p.problem.user_RefId) {
				assigned = this.menu.allUsersList.find((u) => u.user_Id === p.problem.user_RefId);
				user = `${assigned.firstName} ${assigned.lastName}`;
			} else if (p.problem.role_RefId) {
				role = this.menu.problemRoleList.find((r) => r.role_Id === p.problem.role_RefId).name;
			} else {
				this.unassigned = true;
			}

			// set for menu
			const tempAssignGrp = this.menu.problemGroupList.find((g) => g.group_Id === p.problem.group_RefId);
			this.assignedToGroup = {
				active: tempAssignGrp.active,
				email: tempAssignGrp.email, 
				group_Id: tempAssignGrp.group_Id,
				name: tempAssignGrp.name
			}

			this.menu.groupUser
				.filter((gu) => p.problem.group_RefId === gu.group_RefId)
				.map((g) => {
					const user1 = this.menu.allUsersList.find((u) => g.user_RefId === u.user_Id);
					if (user1.active === true) {
						this.newAssignmentList.push({
							id: g.user_RefId,
							type: 'user',
							name: `${user1.firstName} ${user1.lastName}`,
						});
					}
				});

			if (user && !this.newAssignmentList.find((u) => u.name === user)) {
				this.newAssignmentList.push({ id: assigned.user_Id, type: 'user', name: user });
			}

			this.menu.problemRoleList
				.filter((r) => r.group_RefId === p.problem.group_RefId)
				.map((r) =>
					this.newAssignmentList.push({
						id: r.role_Id,
						type: 'role',
						name: r.name,
					}),
				);
			this.newAssignmentList.push({ id: 0, type: 'user', name: 'Unassign' });

			const tmpUsr = this.menu.allUsersList.find((u) => u.user_Id === p.problem.user_RefId);
			if(tmpUsr){
				this.assignedToUserOrRole = {id: tmpUsr.user_Id, type: 'user', name: tmpUsr.firstName + ' ' + tmpUsr.lastName};
			}
			const tmpRole = this.menu.problemRoleList.find((r) => r.role_Id === p.problem.role_RefId);
			if(tmpRole){
				this.assignedToUserOrRole = {id: tmpRole.role_Id, type: 'role', name: tmpRole.name};
			}


			this.assigned = this.newAssignmentList.find((list) => list.name === this.assignedToUserOrRole.name) ?? {
				id: 0,
				type: 'user',
				name: 'Unassign',
			};

		}
	}

	toggleReassignment() {
		this.reassignmentVisible = !this.reassignmentVisible;
		this.originalGroup = this.assignedToGroup;
		this.originalUserOrRole = this.assignedToUserOrRole;
	}

	setAssigned(args: string) {
		this.assigned = this.newAssignmentList.find((list) => list.name === args);
		this.assignedToUserOrRole = this.assigned;
		this.unassigned = args === 'Unassign';
	}

	isNullOrEmpty(obj: any) {
		return obj === null || obj.length === 0;
	}

	isContactFormEmpty() {
		let phones = true;

		const phoneArray = this.newCaseForm
			.get('step4')
			.get('phoneSection')
			.get('phoneNumArray') as UntypedFormArray;

		for (let i = 0; i < phoneArray.length; i++) {
			const phoNum = phoneArray.at(i).get('phoneNum').value;
			if (phoNum.length > 0) {
				phones = false;
				break;
			}
		}

		return (
			this.isNullOrEmpty(this.newCaseForm.value.step4.firstName) &&
			this.isNullOrEmpty(this.newCaseForm.value.step4.lastName) &&
			this.isNullOrEmpty(this.newCaseForm.value.step4.address) &&
			this.isNullOrEmpty(this.newCaseForm.value.step4.email) &&
			this.isNullOrEmpty(this.newCaseForm.value.step4.notes) &&
			phones
		);
	}
}

interface UserOrRole {
	id: number;
	type: 'role' | 'user';
	name: string;
}

export function createFormGroup(problemDescriptionMaxLength: number, fb: UntypedFormBuilder): UntypedFormGroup {
	return new UntypedFormGroup({
		step1: new UntypedFormGroup({
			problemType: new UntypedFormControl(''),
			group: new UntypedFormControl(''),
		}),
		step2: new UntypedFormGroup({
			addressSearch: new UntypedFormControl('', [Validators.required]),
			mapLocation: new UntypedFormControl('', [Validators.required]),
			addressSearchOrigin: new UntypedFormControl(''),
		}),
		step3: new UntypedFormGroup({
			problemSource: new UntypedFormControl(null, [Validators.required]),
			problemReceivedFrom: new UntypedFormControl('', [Validators.required]),
			problemPriority: new UntypedFormControl(''),
			problemDescription: new UntypedFormControl('', [Validators.required, Validators.maxLength(problemDescriptionMaxLength)]),
			notifyAssignees: new UntypedFormControl(true),
			problemSubType: new UntypedFormControl(null),
			assignedToGroup: new UntypedFormControl(null),
			assignedToUserOrRole: new UntypedFormControl(null)
		}),
		step4: new UntypedFormGroup({
			firstName: new UntypedFormControl(''),
			lastName: new UntypedFormControl(''),
			address: new UntypedFormControl(''),
			email: new UntypedFormControl('', [Validators.email]),
			phoneSection: fb.group({
				phoneNumArray: new UntypedFormArray([]),
			}),
			notes: new UntypedFormControl(''),
			companyName: new UntypedFormControl(''),
		}),
	});
}
